"use client"

import * as Sentry from "@sentry/nextjs"
import { useEffect } from "react"

/**
 * Global error boundary — last resort when something throws above
 * the root layout. Cannot import any layout / shared component
 * because by definition we got here BECAUSE the layout failed; this
 * page replaces <html> entirely.
 *
 * Styled to match Aperture-Light (cream surface, emerald accent,
 * mint hairline) so the user lands on something that still feels
 * like DataBridge instead of a stranded dark fallback page.
 *
 * No header / nav imports for the same reason — keep it dependency-
 * light so it can't fail to render in the same conditions that got
 * us here.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          background: "#FAF7EE",
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          color: "#0A0A0A",
        }}
      >
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Ambient cream-on-cream glow — same idea as the home hero
              (radial mint wash) so the error feels like part of the
              same product, not an emergency fallback. */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: "30%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "780px",
              height: "440px",
              background:
                "radial-gradient(ellipse, rgba(15,81,50,0.08) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Subtle grid texture, identical to the landing — keeps the
              page from reading as a flat empty plane. */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "radial-gradient(circle, rgba(10,10,10,0.045) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
              pointerEvents: "none",
            }}
          />

          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "440px",
              borderRadius: "20px",
              background: "#FFFFFF",
              border: "1px solid rgba(10,10,10,0.10)",
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.65) inset, 0 30px 70px -28px rgba(10,10,10,0.18)",
              padding: "36px 32px 32px",
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            {/* Mint hairline accent — the Aperture-Light signature. */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: "32px",
                right: "32px",
                top: 0,
                height: "1px",
                background:
                  "linear-gradient(90deg, transparent, rgba(15,81,50,0.45), transparent)",
                pointerEvents: "none",
              }}
            />

            {/* Trace eyebrow — pairs with the editorial voice on the
                rest of the site. */}
            <p
              style={{
                margin: "0 0 18px",
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: "10.5px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(10,10,10,0.45)",
                fontWeight: 700,
              }}
            >
              <span style={{ color: "#0F5132", marginRight: "8px" }}>—</span>
              Error · {error.digest ?? "untraced"}
            </p>

            {/* Icon badge in emerald, matching every other "important"
                surface (paywall, audit trail anchor, CTA). */}
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "rgba(15,81,50,0.06)",
                border: "1px solid rgba(15,81,50,0.20)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0F5132"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h1
              style={{
                margin: "0 0 10px",
                fontSize: "22px",
                fontWeight: 800,
                color: "#0A0A0A",
                letterSpacing: "-0.025em",
                lineHeight: 1.2,
              }}
            >
              Something went wrong
            </h1>

            <p
              style={{
                margin: "0 0 26px",
                fontSize: "14px",
                color: "rgba(10,10,10,0.62)",
                lineHeight: 1.55,
              }}
            >
              An unexpected error broke the page. Our team has been notified and
              is working on it. You can try again, head home, or email{" "}
              <a
                href="mailto:support@databridge.so"
                style={{
                  color: "#0F5132",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
              >
                support@databridge.so
              </a>{" "}
              if it persists.
            </p>

            {/* Actions — same emerald gradient + ghost pair as every
                primary/secondary CTA pair across the site. */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={reset}
                style={{
                  padding: "10px 18px",
                  borderRadius: "12px",
                  fontSize: "13.5px",
                  fontWeight: 700,
                  color: "#FAF7EE",
                  background:
                    "linear-gradient(135deg, #1F7A4E 0%, #0F5132 50%, #0A3D26 100%)",
                  border: "none",
                  cursor: "pointer",
                  boxShadow:
                    "0 14px 32px -12px rgba(15,81,50,0.45), 0 0 0 1px rgba(255,255,255,0.06) inset",
                }}
              >
                Try again
              </button>
              <a
                href="/"
                style={{
                  padding: "10px 18px",
                  borderRadius: "12px",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: "rgba(10,10,10,0.65)",
                  background: "#FBF8F0",
                  border: "1px solid rgba(10,10,10,0.10)",
                  cursor: "pointer",
                  textDecoration: "none",
                  display: "inline-block",
                }}
              >
                Go home
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
